
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Share, Info, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Card } from '@/components/ui/card';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPWA: React.FC = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  // Check if the user is on iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsAppInstalled(true);
    }

    // Capture the install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Handle app installed event
    window.addEventListener('appinstalled', () => {
      setIsAppInstalled(true);
      setInstallPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    
    // Show the install prompt
    await installPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const choiceResult = await installPrompt.userChoice;
    
    if (choiceResult.outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
    
    // Clear the saved prompt as it can't be used again
    setInstallPrompt(null);
  };

  const toggleIOSInstructions = () => {
    setShowIOSInstructions(!showIOSInstructions);
  };

  // If app is installed, don't show anything
  if (isAppInstalled) {
    return null;
  }

  // For iOS devices
  if (isIOS) {
    return (
      <>
        {/* Floating icon button */}
        <Button
          variant="secondary"
          size="icon"
          onClick={toggleIOSInstructions}
          className="fixed bottom-24 right-4 z-50 rounded-full shadow-lg bg-blue-500 text-white h-12 w-12 hover:bg-blue-600"
        >
          <Info size={20} />
        </Button>

        {/* Instructions card that appears when the icon is clicked */}
        {showIOSInstructions && (
          <Card className="fixed bottom-40 right-4 left-4 md:left-auto md:w-80 z-50 p-4 shadow-lg animate-in fade-in zoom-in duration-300">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">Add to Home Screen</h3>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={toggleIOSInstructions}
                className="h-6 w-6"
              >
                <X size={16} />
              </Button>
            </div>
            
            <div className="text-sm space-y-2">
              <p>To install WeTrippy on your iPhone:</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Tap the <Share size={14} className="inline mb-1 mx-1" /> share button</li>
                <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                <li>Tap <strong>"Add"</strong> in the top right corner</li>
              </ol>
            </div>
          </Card>
        )}
      </>
    );
  }
  
  // For non-iOS devices that support PWA install, show an install button on home page
  if (installPrompt && isHomePage) {
    return (
      <Button 
        onClick={handleInstallClick} 
        variant="default"
        className="bg-blue-500 hover:bg-blue-600 fixed bottom-4 right-4 z-50 flex items-center gap-1 shadow-lg"
      >
        <Download size={16} />
        Install App
      </Button>
    );
  } 
  // For non-homepage views, show a smaller button for non-iOS
  else if (installPrompt) {
    return (
      <Button 
        onClick={handleInstallClick} 
        variant="outline" 
        size="sm" 
        className="flex items-center gap-1 bg-white/90 backdrop-blur-sm fixed bottom-4 right-4 z-50 shadow-lg"
      >
        <Download size={16} />
        Install App
      </Button>
    );
  }

  return null;
};

export default InstallPWA;
